import React from 'react'
import styled from '@emotion/styled'
import { useRouter } from 'next/router'
import { useAppSelector } from '@/app/hooks'
import { selectFilter } from '@/app/reducers/filters'
import { constructUrlFromFilters } from '@/components/templates/filters/util'
import { UrlSlug } from '@toctoc/lista-seo-common'
import { IFiltros } from '@/components/templates/search/interfaces/IEstructuraPrincipal'

interface FilterChipsProps {
	urlSlug?: Array<{ idType: UrlSlug }>;
	onRemoveFilter?: (filterId: string) => void;
	isMobile?: boolean;
}

const getChipLabel = (filter: IFiltros): string => {
	if (!filter.values || filter.values.length === 0) return filter.id ?? ''

	const firstValue = filter.values[0]

	if (firstValue.label) return firstValue.label

	if (firstValue.value && firstValue.value.length > 0) {
		return firstValue.value.map(String).join(', ')
	}

	return filter.id ?? ''
}

export const FilterChips = ({ urlSlug, onRemoveFilter, isMobile = false }: FilterChipsProps) => {
	const router = useRouter()
	const filterState = useAppSelector(selectFilter)
	const activeFilters: IFiltros[] = filterState.filtros ?? []

	const handleRemove = (filterId: string) => {
		if (onRemoveFilter) {
			onRemoveFilter(filterId)
			return
		}
		// fallback: manipulación directa de URL (solo si no hay callback)
		const newFilters = activeFilters.filter(f => f.id !== filterId)
		const [newUrl] = constructUrlFromFilters(newFilters, urlSlug) ?? []
		if (newUrl) {
			router.push(newUrl, undefined, { shallow: true })
		} else {
			const pathOnly = router.asPath.split('?')[0]
			router.push(pathOnly, undefined, { shallow: true })
		}
	}

	if (activeFilters.length === 0) return null

	const displayFilters = isMobile ? [...activeFilters].reverse() : activeFilters

	return (
		<ChipsRow scrollable={isMobile}>
			{displayFilters.map((filter, index) => {
				const label = getChipLabel(filter)
				const filterId = filter.id ?? ''
				return (
					<Chip key={filterId || index}>
						<ChipLabel>{label}</ChipLabel>
						<ChipRemove
							type="button"
							onClick={() => filterId && handleRemove(filterId)}
							aria-label={`Eliminar filtro ${label}`}
						>
							×
						</ChipRemove>
					</Chip>
				)
			})}
		</ChipsRow>
	)
}

/* ─── Styled Components ─────────────────────────────────────── */

const ChipsRow = styled.div<{ scrollable?: boolean }>`
	display: flex;
	flex-wrap: ${({ scrollable }) => scrollable ? 'nowrap' : 'wrap'};
	gap: 8px;
	margin-bottom: 16px;
	${({ scrollable }) => scrollable && `
		overflow-x: auto;
		-webkit-overflow-scrolling: touch;
		scrollbar-width: none;
		padding-bottom: 4px;
		&::-webkit-scrollbar { display: none; }
	`}
`

const Chip = styled.div`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	background: #EDE8FF;
	border: 1px solid #C0ACFF;
	border-radius: 20px;
	padding: 4px 10px 4px 12px;
	font-family: nunito, sans-serif;
	font-size: 13px;
	color: #3200C1;
	font-weight: 600;
	white-space: nowrap;
	flex-shrink: 0;
`

const ChipLabel = styled.span`
	line-height: 1;
`

const ChipRemove = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	color: #3200C1;
	font-size: 16px;
	line-height: 1;
	cursor: pointer;
	padding: 0 2px;
	margin-left: 2px;

	&:hover {
		color: #7B00FF;
	}
`
